import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCancelSubscriptionMutation, useKeepSubscriptionMutation } from "@/store/rest_api";
import Organisation from "@/model/interface/Organisation";
import { SubscriptionPlan } from "@/model/interface/SubscriptionPlan";
import { StripeSubscriptionData } from "@/model/interface/StripeSubscriptionData";
import { apiErrorMessage } from "@/util/apiError";

interface PlanHeaderProps {
  organisation: Organisation;
  currentPlan?: SubscriptionPlan;
  subscription?: StripeSubscriptionData;
  onSwitchToAnnual: () => void;
}

interface HeaderState {
  title: string;
  pill?: { label: string; className: string };
  line?: string | null;
}

export function PlanHeader({ organisation, currentPlan, subscription, onSwitchToAnnual }: PlanHeaderProps) {
  const navigate = useNavigate();
  const [cancelSubscription, { isLoading: isCanceling }] = useCancelSubscriptionMutation();
  const [keepSubscription, { isLoading: isKeeping }] = useKeepSubscriptionMutation();
  const [confirmCancel, setConfirmCancel] = useState(false);

  const status = organisation.subscriptionStatus;
  const isAnnual = organisation.billingInterval === "ANNUAL";
  const cancelScheduled = !!subscription?.cancelAtPeriodEnd;
  const periodEnd = subscription?.currentPeriodEnd
    ? format(new Date(subscription.currentPeriodEnd * 1000), "MMM d, yyyy")
    : null;
  const annualSavings = currentPlan?.annualBasePrice ? currentPlan.monthlyBasePrice * 12 - currentPlan.annualBasePrice : 0;

  const formatDate = (value?: string | null) => (value ? format(parseISO(value), "MMM d, yyyy") : null);

  const handleCancel = async () => {
    try {
      await cancelSubscription().unwrap();
      toast.info("Your subscription will be canceled at the end of the billing period");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Failed to cancel subscription"));
    }
  };

  const handleKeep = async () => {
    try {
      await keepSubscription().unwrap();
      toast.success("Your plan will renew as usual");
    } catch (error) {
      toast.error(apiErrorMessage(error, "Could not keep your plan"));
    }
  };

  const planLine = () => {
    if (!currentPlan) {
      return null;
    }
    const price =
      currentPlan.pricingMode === "AT_COST"
        ? `$0 per month after a 100% discount, usage at carrier rates`
        : isAnnual && currentPlan.annualBasePrice
          ? `$${currentPlan.annualBasePrice} per year`
          : `$${currentPlan.monthlyBasePrice} per month`;
    const validUntil = formatDate(organisation.planValidUntil);
    if (validUntil) {
      const planReturn = organisation.planReturn;
      const returnsTo = planReturn?.plan ? planReturn.planName ?? planReturn.plan : "your trial";
      return `${price} · Valid until ${validUntil}, then back to ${returnsTo}`;
    }
    if (!periodEnd) {
      return price;
    }
    return cancelScheduled ? `${price} · You keep access until ${periodEnd}` : `${price} · Renews ${periodEnd}`;
  };

  const headerState = (): HeaderState => {
    if (currentPlan) {
      const title = `${currentPlan.name} plan`;
      if (status === "PAST_DUE") {
        return { title, pill: { label: "Payment failed", className: "status-pill-destructive" }, line: planLine() };
      }
      if (cancelScheduled) {
        return {
          title,
          pill: { label: periodEnd ? `Cancels ${periodEnd}` : "Cancel scheduled", className: "status-pill-warning" },
          line: planLine(),
        };
      }
      return { title, pill: { label: "Active", className: "status-pill-success" }, line: planLine() };
    }
    switch (status) {
      case "TRIAL": {
        const trialEnd = formatDate(organisation.trialEndDate);
        return {
          title: "Choose a plan",
          pill: { label: "Trial", className: "status-pill-info" },
          line: trialEnd
            ? `Your trial ends ${trialEnd}. Pick a plan to keep your numbers, contacts and history.`
            : "Pick a plan to keep your numbers, contacts and history.",
        };
      }
      case "TRIAL_LAPSED":
        return {
          title: "Choose a plan",
          pill: { label: "Trial ended", className: "status-pill-warning" },
          line: "Your trial has ended. Pick a plan to get back in.",
        };
      case "CANCELED": {
        const endedOn = formatDate(organisation.subscriptionCanceledDate);
        return {
          title: "Reactivate your plan",
          pill: { label: "Canceled", className: "status-pill-neutral" },
          line: endedOn ? `Your plan ended on ${endedOn}.` : "Your plan has ended.",
        };
      }
      case "SUSPENDED":
        return {
          title: "Choose a plan",
          pill: { label: "Suspended", className: "status-pill-destructive" },
          line: "Your subscription is suspended. Subscribe to a plan to restore access, and check your card on Overview if a recent charge failed.",
        };
      default:
        return { title: "Choose a plan", line: "Select the plan that fits your business." };
    }
  };

  const { title, pill, line } = headerState();
  const graceUntil = formatDate(organisation.subscriptionGraceUntil);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
            {pill && <span className={pill.className}>{pill.label}</span>}
          </div>
          {line && <p className="text-muted-foreground tabular-nums">{line}</p>}
        </div>
        {currentPlan && (
          <div className="flex items-center gap-2">
            {cancelScheduled ? (
              <Button onClick={handleKeep} disabled={isKeeping}>
                Keep my plan
              </Button>
            ) : (
              status === "ACTIVE" && (
                <>
                  <Button
                    variant="ghost"
                    className="text-muted-foreground hover:text-destructive"
                    disabled={isCanceling}
                    onClick={() => setConfirmCancel(true)}
                  >
                    Cancel subscription
                  </Button>
                  {!isAnnual && annualSavings > 0 && (
                    <Button variant="outline" onClick={onSwitchToAnnual}>
                      Switch to annual, save ${annualSavings}
                    </Button>
                  )}
                </>
              )
            )}
          </div>
        )}
      </div>

      {currentPlan && status === "PAST_DUE" && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {graceUntil
                ? `Your last payment failed. Update your card by ${graceUntil} to keep access.`
                : "Your last payment failed. Update your card to keep access."}
            </span>
            <Button size="sm" variant="outline" onClick={() => navigate("/manage-organisation/billing/overview")}>
              Update card
            </Button>
          </AlertDescription>
        </Alert>
      )}

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              You keep access until the end of your billing period and can undo this until then. After that the
              subscription ends. The workspace owner can{" "}
              <Link to="/manage-organisation/settings/export" className="underline underline-offset-4">
                export all your data
              </Link>{" "}
              before then, and for 72 hours after it ends.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleCancel()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Cancel at Period End
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
